import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PrecisionManufacturing as MachineIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrowRounded as PlayIcon,
  TaskAltOutlined as CompleteIcon,
  HourglassBottomRounded as WaitingIcon,
  StarOutlineRounded as StarIcon,
  SendOutlined as SubmitIcon,
  CheckCircleOutline as CheckIcon,
  VisibilityOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Offer, OfferStatusCode } from "../models/models";
import { createOfferStatusLabelMap } from "../utils/offerStatusMaps";
import { dt } from "../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import { getProviderLifecycleStages } from "../../shared/utils/lifecycleStages";
import { statusBadgeColors, getGuidanceInfo, GuidanceText } from "./OfferCard";
import dayjs from "dayjs";
import { formatUiDate } from "../../../shared/utils/dateFormat";

// -- Props (same shape as OfferCard) --
interface OfferCardCompactProps {
  item: Offer;
  index: number;
  formatCurrency: (value: number) => string;
  onClick: (item: Offer) => void;
  onConfirmService?: (item: Offer) => void;
  onStartWork?: (item: Offer) => void;
  onMarkComplete?: (item: Offer) => void;
  onReview?: (item: Offer) => void;
  onSubmit?: (item: Offer) => void;
  onWithdraw?: (item: Offer) => void;
}

// -- Helpers (same logic as OfferCard) --
const getExpiryInfo = (item: Offer) => {
  if (!item.expiresAt) return { isExpiringSoon: false, isExpired: false, hoursLeft: 0 };
  const now = dayjs(); const expires = dayjs(item.expiresAt);
  const hoursLeft = expires.diff(now, "hour", true);
  const active = item.statusCode === OfferStatusCode.sent || item.statusCode === OfferStatusCode.viewed;
  if (hoursLeft <= 0 && active) return { isExpiringSoon: false, isExpired: true, hoursLeft: 0 };
  if (hoursLeft > 0 && hoursLeft <= 48 && active) return { isExpiringSoon: true, isExpired: false, hoursLeft: Math.ceil(hoursLeft) };
  return { isExpiringSoon: false, isExpired: false, hoursLeft: 0 };
};

const getDeadlineInfo = (item: Offer) => {
  if (item.statusCode !== OfferStatusCode.awaitingConfirmation || !item.confirmationDeadline)
    return { show: false, isOverdue: false, isSameDay: false, label: "" };
  const now = dayjs(); const dl = dayjs(item.confirmationDeadline); const ms = dl.diff(now);
  if (ms <= 0) return { show: true, isOverdue: true, isSameDay: false, label: "" };
  const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
  return { show: true, isOverdue: false, isSameDay: dl.isSame(now, "day"), label: h > 0 ? `${h}h ${m}m` : `${m}m` };
};

// -- Pill CTA (24px radius, gradient support) --
const PillCTA: React.FC<{
  label: string; color: string; hoverColor?: string; onClick?: () => void;
  icon?: React.ReactNode; outline?: boolean; pulse?: boolean; gradient?: boolean;
}> = ({ label, color, hoverColor, onClick, icon, outline, pulse, gradient }) => (
  <Box
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    sx={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
      ...(gradient ? { background: "linear-gradient(135deg, #3B82F6, #2563EB)", color: "#FFF" }
        : { backgroundColor: outline ? "transparent" : color, color: outline ? color : "#FFF" }),
      border: outline ? `1.5px solid ${color}` : "none", borderRadius: "24px",
      padding: "9px 16px", fontSize: "12px", fontWeight: 700, fontFamily: dt.font,
      cursor: "pointer", mt: "auto", transition: "all 0.2s ease",
      // L6 (USER-LANG-IT-PL-1): wraps instead of overflowing the compact card (DE CTA labels up to ~280px).
      whiteSpace: "normal", textAlign: "center", lineHeight: 1.3, maxWidth: "100%",
      ...(pulse && { animation: "offerRing 2s ease-in-out infinite" }),
      "&:hover": {
        backgroundColor: outline ? `${color}14` : (hoverColor || color),
        transform: "translateY(-1px)",
        ...(outline && { borderColor: hoverColor || color }),
        ...(gradient && { background: "linear-gradient(135deg, #2563EB, #1D4ED8)" }),
      },
    }}
  >
    {icon}{label}<ChevronRightIcon sx={{ fontSize: "14px" }} />
  </Box>
);

// -- Label style shorthand --
const labelSx = { fontSize: "10px", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, whiteSpace: "nowrap" as const, flexShrink: 0 };

// -- OfferCardCompact --
const OfferCardCompact: React.FC<OfferCardCompactProps> = ({
  item, index, formatCurrency, onClick,
  onConfirmService, onStartWork, onMarkComplete, onReview, onSubmit, onWithdraw,
}) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");
  const c = statusBadgeColors[item.statusCode] || statusBadgeColors[OfferStatusCode.withdrawn];
  const statusLabelMap = createOfferStatusLabelMap(translate);
  const serviceDate = item.confirmedArrivalDate || item.earliestStartDate;
  const expiry = getExpiryInfo(item);
  const dl = getDeadlineInfo(item);
  const tf = item.confirmedArrivalTimeFrom?.slice(0, 5);
  const tt = item.confirmedArrivalTimeTo?.slice(0, 5);
  const tw = tf && tt ? `${tf}\u2013${tt}` : "";
  const viewed = (item.statusCode === OfferStatusCode.sent || item.statusCode === OfferStatusCode.viewed) && !!item.viewedDate;
  const machine = item.serviceRequest?.machine;
  const machineSrc = machine?.imageUrl || machine?.catalogImageUrl;
  const machineName = machine?.brandName ? `${machine.brandName} ${machine.name}` : machine?.name;

  const guidance = getGuidanceInfo(item, translate);

  const renderCTA = () => {
    const S = OfferStatusCode;
    const waitingPill = (label: string, color: string) => (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", backgroundColor: `${color}14`, color, border: `1px solid ${color}33`, borderRadius: "24px", padding: "9px 16px", fontSize: "12px", fontWeight: 600, fontFamily: dt.font, mt: "auto", whiteSpace: "nowrap" }}>
        <WaitingIcon sx={{ fontSize: 14, animation: "spin 2s linear infinite" }} />{label}
      </Box>
    );
    switch (item.statusCode) {
      case S.awaitingConfirmation: return <PillCTA label={translate("confirmService")} color="#F59E0B" hoverColor="#D97706" onClick={() => onConfirmService?.(item)} pulse />;
      case S.confirmed: return <PillCTA label={translate("inlineActions.startWork")} color="#14B8A6" hoverColor="#0D9488" onClick={() => onStartWork?.(item)} icon={<PlayIcon sx={{ fontSize: 14 }} />} />;
      case S.workInProgress: return <PillCTA label={translate("inlineActions.complete")} color="#3B82F6" hoverColor="#2563EB" onClick={() => onMarkComplete?.(item)} icon={<CompleteIcon sx={{ fontSize: 14 }} />} gradient />;
      case S.workCompleted: return waitingPill(translate("inlineActions.waiting"), "#16A34A");
      case S.serviceCompleted:
        if (item.reviewRating != null) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", backgroundColor: "#05966914", color: "#059669", border: "1px solid #05966933", borderRadius: "24px", padding: "9px 16px", fontSize: "12px", fontWeight: 600, fontFamily: dt.font, mt: "auto", whiteSpace: "nowrap" }}>
              <CheckIcon sx={{ fontSize: 14 }} />{translate("card.reviewReceived")}
            </Box>
          );
        }
        return waitingPill(translate("card.awaitingReview"), "#CA8A04");
      case S.sent: return waitingPill(translate("card.awaitingResponse"), "#6B7280");
      case S.viewed: return waitingPill(translate("card.underReview"), "#6B7280");
      case S.draft: return <PillCTA label={translate("submitOffer")} color="#8E44AD" hoverColor="#7D3C98" onClick={() => onSubmit?.(item)} icon={<SubmitIcon sx={{ fontSize: 14 }} />} />;
      default: return null;
    }
  };

  return (
    <Box
      onClick={() => onClick(item)}
      sx={{
        backgroundColor: dt.cardBg, borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${c.text}`, cursor: "pointer", boxShadow: dt.cardShadow, overflow: "hidden",
        width: "100%", boxSizing: "border-box", transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4,0,0.2,1) ${index * 0.04}s both`,
        "@keyframes cardReveal": { "0%": { opacity: 0, transform: "translateY(12px) scale(0.98)" }, "100%": { opacity: 1, transform: "translateY(0) scale(1)" } },
        "&:hover": { boxShadow: dt.cardShadowHover, transform: "translateY(-1px)" },
        display: "flex", flexDirection: isPhone ? "column" : "row",
        ...(expiry.isExpired && { opacity: 0.65 }),
      }}
    >
      {/* LEFT ZONE */}
      <Box sx={{ flex: 1, padding: isPhone ? "12px" : "14px 18px 14px 16px", minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
        {/* Row 1: Issue headline + OFR ref */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", mb: "4px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
            <Box sx={{ color: dt.textPrimary, fontWeight: 700, fontSize: "16px", fontFamily: dt.font, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {item.serviceRequest?.issue || "\u2014"}
            </Box>
            {expiry.isExpiringSoon && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", backgroundColor: "#FEF9E7", color: "#F59E0B", borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 600, fontFamily: dt.fontMono, whiteSpace: "nowrap", flexShrink: 0 }}>
                <TimerOutlined sx={{ fontSize: "12px" }} />{translate("card.expiresIn", { hours: expiry.hoursLeft })}
              </Box>
            )}
            {expiry.isExpired && (
              <Box sx={{ display: "inline-flex", alignItems: "center", backgroundColor: dt.inputBg, color: dt.textMuted, borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, fontFamily: dt.fontMono, whiteSpace: "nowrap", flexShrink: 0 }}>
                {translate("card.expiredLabel")}
              </Box>
            )}
          </Box>
          <Box sx={{ color: dt.textSecondary, fontSize: "11px", fontFamily: dt.fontMono, letterSpacing: "0.2px", whiteSpace: "nowrap", flexShrink: 0 }}>
            {item.internalNumber}{item.createdDate && <>{" \u00B7 "}{formatUiDate(item.createdDate)}</>}
          </Box>
        </Box>
        {/* Row 2: Company (no label) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
          {item.customer?.logoUrl ? (
            <Box component="img" src={item.customer.logoUrl} alt="" sx={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `1px solid ${dt.cardBorder}` }} />
          ) : (
            <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: dt.blueSoft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Box sx={{ fontSize: "11px", fontWeight: 700, color: dt.blue, fontFamily: dt.font }}>{item.customer?.name?.charAt(0)?.toUpperCase() || "?"}</Box>
            </Box>
          )}
          <Box
            component="span"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (item.customer?.id) navigate(`/companies/${item.customer.id}`);
            }}
            sx={{ color: dt.textLink, fontWeight: 500, fontSize: "13px", fontFamily: dt.font, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >{item.customer?.name}</Box>
          {viewed && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", color: dt.textMuted, fontSize: "11px", fontFamily: dt.font, whiteSpace: "nowrap", flexShrink: 0, ml: "auto" }}>
              <VisibilityOutlined sx={{ fontSize: "13px" }} />{translate("card.seen", { date: formatUiDate(item.viewedDate, "dayMonth") })}
            </Box>
          )}
        </Box>
        {/* Row 3: Machine (no label) */}
        {machine && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
            {machineSrc ? (
              <Box component="img" src={machineSrc} alt="" sx={{ width: 36, height: 36, borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`, objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <Box sx={{ width: 36, height: 36, borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`, backgroundColor: dt.inputBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MachineIcon sx={{ fontSize: 18, color: dt.textMuted }} />
              </Box>
            )}
            <Box component="span" sx={{ color: dt.textLink, fontSize: "12px", fontFamily: dt.font, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
              {machineName}
            </Box>
          </Box>
        )}
        {/* Row 4: Service date + Price */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {serviceDate ? (
            <Box sx={{ fontSize: "12px", fontFamily: dt.font, display: "flex", alignItems: "center", gap: "4px" }}>
              <Box component="span" sx={labelSx}>{translate("card.serviceDate")}</Box>
              <Box component="span" sx={{ color: dt.textPrimary, fontWeight: 600 }}>{formatUiDate(serviceDate)}</Box>
              {tw && <Box component="span" sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "11px" }}>{"\u00B7 "}{tw}</Box>}
            </Box>
          ) : <Box />}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Box component="span" sx={labelSx}>{translate("card.price")}</Box>
            <Box sx={{ backgroundColor: "#F0FDF4", padding: "3px 10px", borderRadius: "6px" }}>
              <Box component="span" sx={{ color: dt.textPrimary, fontWeight: 700, fontSize: "15px", fontFamily: dt.fontMono, whiteSpace: "nowrap" }}>{formatCurrency(item.totalEstimatedCost)}</Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* RIGHT ZONE (200px fixed) */}
      <Box sx={{ flex: isPhone ? "none" : "0 0 200px", maxWidth: isPhone ? "100%" : "200px", display: "flex", flexDirection: "column", padding: "14px", borderLeft: isPhone ? "none" : `1px solid ${dt.cardBorder}`, borderTop: isPhone ? `1px solid ${dt.cardBorder}` : "none", gap: "6px" }}>
        <RedesignStatusBadge label={statusLabelMap[item.statusCode] || ""} colorConfig={c} />
        {guidance && (
          <Box
            sx={{
              fontSize: "11px",
              color: dt.textSecondary,
              fontFamily: dt.font,
              lineHeight: 1.4,
            }}
          >
            <GuidanceText text={guidance.text} boldName={guidance.boldName} />
          </Box>
        )}
        {dl.show && (
          <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.fontMono, color: dl.isOverdue ? "#DC2626" : dl.isSameDay ? "#F59E0B" : dt.textSecondary, display: "flex", alignItems: "center", gap: "4px" }}>
            <TimerOutlined sx={{ fontSize: "13px" }} />
            {dl.isOverdue ? translate("card.confirmOverdue") : translate("card.confirmWithin", { time: dl.label })}
          </Box>
        )}
        {item.statusCode !== OfferStatusCode.draft && (
          <ServiceLifecycleTracker stages={getProviderLifecycleStages(item.statusCode)} variant="compact" />
        )}
        {renderCTA()}
      </Box>
    </Box>
  );
};

export default OfferCardCompact;
